import {
  CashAccounts,
  IncomeEvent,
  OwnerPayConfig,
  RecurringIncome,
} from "../types";

/**
 * Cash-flow API client. Mirrors the shape of requests/budget.ts (Firebase bearer,
 * JSON). These endpoints do not exist on the server yet — see
 * docs/CASHFLOW_API.md for the contract. Nothing here is called unless
 * REACT_APP_CASHFLOW_API === "true" (see functions/cashflowSync.ts); until then
 * the atoms persist to localStorage.
 */

const api = process.env.REACT_APP_API;

export const isCashflowApiEnabled = () =>
  process.env.REACT_APP_CASHFLOW_API === "true";

const authHeaders = (accessToken: string) => ({
  Authorization: `Bearer ${accessToken}`,
  "Content-type": "application/json",
});

export const getCashAccounts = async (
  accessToken: string,
): Promise<CashAccounts> => {
  try {
    const response = await fetch(`${api}/cashflow/accounts`, {
      method: "GET",
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to get cash accounts");
  }
};

export const updateCashAccounts = async (
  accessToken: string,
  accounts: CashAccounts,
) => {
  try {
    const response = await fetch(`${api}/cashflow/accounts`, {
      method: "PUT",
      body: JSON.stringify({ ...accounts }),
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to update cash accounts");
  }
};

export const getIncomeEvents = async (
  accessToken: string,
): Promise<IncomeEvent[]> => {
  try {
    const response = await fetch(`${api}/cashflow/income`, {
      method: "GET",
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to get income events");
  }
};

export const createIncomeEvent = async (
  accessToken: string,
  event: IncomeEvent,
) => {
  try {
    const response = await fetch(`${api}/cashflow/income`, {
      method: "POST",
      body: JSON.stringify({ ...event }),
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to create income event");
  }
};

export const updateIncomeEvent = async (
  accessToken: string,
  event: IncomeEvent,
) => {
  try {
    const response = await fetch(`${api}/cashflow/income/${event.id}`, {
      method: "PUT",
      body: JSON.stringify({ ...event }),
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to update income event");
  }
};

export const deleteIncomeEvent = async (accessToken: string, id: string) => {
  try {
    const response = await fetch(`${api}/cashflow/income/${id}`, {
      method: "DELETE",
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to delete income event");
  }
};

export const getRecurringIncome = async (
  accessToken: string,
): Promise<RecurringIncome[]> => {
  try {
    const response = await fetch(`${api}/cashflow/recurring`, {
      method: "GET",
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to get recurring income");
  }
};

export const createRecurringIncome = async (
  accessToken: string,
  rule: RecurringIncome,
) => {
  try {
    const response = await fetch(`${api}/cashflow/recurring`, {
      method: "POST",
      body: JSON.stringify({ ...rule }),
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to create recurring income");
  }
};

export const updateRecurringIncome = async (
  accessToken: string,
  rule: RecurringIncome,
) => {
  try {
    const response = await fetch(`${api}/cashflow/recurring/${rule.id}`, {
      method: "PUT",
      body: JSON.stringify({ ...rule }),
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to update recurring income");
  }
};

export const deleteRecurringIncome = async (
  accessToken: string,
  id: string,
) => {
  try {
    const response = await fetch(`${api}/cashflow/recurring/${id}`, {
      method: "DELETE",
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to delete recurring income");
  }
};

export const getOwnerPayConfig = async (
  accessToken: string,
): Promise<OwnerPayConfig> => {
  try {
    const response = await fetch(`${api}/cashflow/config`, {
      method: "GET",
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to get owner pay config");
  }
};

export const updateOwnerPayConfig = async (
  accessToken: string,
  config: OwnerPayConfig,
) => {
  try {
    const response = await fetch(`${api}/cashflow/config`, {
      method: "PUT",
      body: JSON.stringify({ ...config }),
      headers: authHeaders(accessToken),
    });

    return await response.json();
  } catch (err) {
    throw new Error("Failed to update owner pay config");
  }
};
